import { useEffect, useRef } from 'react';
import ApexCharts from 'apexcharts';
import Tooltip from '@components/common/Tooltip';

const Chart = () => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);

    useEffect(() => {
        if (!chartRef.current) return;

        // 최근 14일 날짜 생성
        const getLast14Days = () => {
            const dates = [];
            const today = new Date();
            for (let i = 13; i >= 0; i--) {
                const date = new Date(today);
                date.setDate(today.getDate() - i);
                dates.push(`${date.getMonth() + 1}/${date.getDate()}`);
            }
            return dates;
        };

        // 생육 단계별 성장 곡선 시뮬레이션
        const generateGrowthData = (
            initialWeight: number,
            growthRate: number,
            variance: number,
        ) => {
            const data = [];
            let currentWeight = initialWeight;

            for (let i = 0; i < 14; i++) {
                // 초기에는 느린 성장, 중기에는 빠른 성장, 후기에는 안정화
                const stageFactor = Math.sin((i / 14) * Math.PI) * 1.5 + 0.5;
                const dailyGrowth = currentWeight * growthRate * stageFactor;
                const randomVariance = (Math.random() - 0.5) * variance;

                currentWeight += dailyGrowth + randomVariance;
                data.push(Math.round(currentWeight * 10) / 10);
            }
            return data;
        };

        const options = {
            series: [
                {
                    name: '뿌리 무게',
                    data: generateGrowthData(8, 0.06, 0.5), // 초기 8g, 일일 6% 성장률, 변동폭 0.5g
                },
                {
                    name: '줄기 무게',
                    data: generateGrowthData(12, 0.07, 0.8), // 초기 12g, 일일 7% 성장률, 변동폭 0.8g
                },
                {
                    name: '잎 무게',
                    data: generateGrowthData(15, 0.08, 1.2), // 초기 15g, 일일 8% 성장률, 변동폭 1.2g
                },
                {
                    name: '수확물 무게',
                    data: generateGrowthData(20, 0.09, 1.5), // 초기 20g, 일일 9% 성장률, 변동폭 1.5g
                },
            ],
            chart: {
                type: 'bar',
                height: 321,
                stacked: true,
                toolbar: {
                    show: false,
                },
                zoom: {
                    enabled: true,
                },
                dropShadow: {
                    enabled: true,
                    enabledOnSeries: undefined,
                    top: 3,
                    left: 0,
                    blur: 4,
                    color: '#000',
                    opacity: 0.1,
                },
            },
            grid: {
                borderColor: '#f1f1f1',
                strokeDashArray: 3,
            },
            yaxis: {
                title: {
                    text: '무게 (g)',
                },
                labels: {
                    formatter: function (val: number) {
                        return val.toFixed(1) + 'g';
                    },
                },
            },
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        legend: {
                            position: 'bottom',
                            offsetX: -10,
                            offsetY: 0,
                        },
                    },
                },
            ],
            colors: [
                '#b59468', // 뿌리 무게 - 주황색
                '#69ac53', // 줄기 무게 - 파란색
                '#2ddf6b', // 잎 무게 - 녹색
                '#d3dc99', // 수확물 무게 - 빨간색
            ],
            legend: {
                show: true,
                position: 'top',
                markers: {
                    size: 5,
                    shape: 'circle',
                },
            },
            stroke: {
                curve: 'smooth',
                width: [0, 0, 0, 0],
            },
            plotOptions: {
                bar: {
                    columnWidth: '60%',
                    borderRadius: 2,
                },
            },
            dataLabels: {
                enabled: false,
            },
            xaxis: {
                categories: getLast14Days(),
                labels: {
                    rotate: -45,
                    rotateAlways: false,
                },
            },
            fill: {
                opacity: 1,
            },
            tooltip: {
                y: {
                    formatter: function (val: number) {
                        return val.toFixed(1) + 'g';
                    },
                },
            },
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, []);

    return (
        <>
            <div className="custom-card card">
                <div className="card-header justify-content-between align-items-center d-sm-flex d-block">
                    <div className="card-title mb-sm-0 mb-2">
                        생육무게정보
                        <Tooltip
                            content="도움말이 표시될예정"
                            customClass="tooltip-dark"
                            placement="top"
                        >
                            <i className="ri-question-line" />
                        </Tooltip>
                    </div>
                    <div
                        aria-label="Basic example"
                        role="group"
                        className="flex-wrap btn-group"
                    >
                        <button
                            type="button"
                            className="btn-wave btn btn-success"
                        >
                            14일
                        </button>
                        <button
                            type="button"
                            className="btn-wave btn btn-success-light"
                        >
                            한달
                        </button>
                        <button
                            type="button"
                            className="btn-wave btn btn-success-light"
                        >
                            6개월
                        </button>
                        <button
                            type="button"
                            className="btn-wave btn btn-success-light"
                        >
                            1년
                        </button>
                        <button
                            type="button"
                            className="btn-wave btn btn-success-light"
                        >
                            더보기
                        </button>
                    </div>
                </div>
                <div className="card-body">
                    <div ref={chartRef} style={{}}></div>
                </div>
            </div>
        </>
    );
};

export default Chart;
